import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { PenLine } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../utils/authUtils';

/**
 * Public Author Bio card — visible only to admins & contributors. Shown
 * underneath blog posts written by this user. State (display name, photo,
 * credentials, bio textarea) is owned by the parent so the existing Save
 * handler can persist everything in a single request.
 *
 * The parent must guard rendering: only mount when
 * `user.is_admin || user.is_contributor`.
 */
export default function AccountAuthorBioCard({
  user,
  isSpanish = false,
  authorDisplayName,
  setAuthorDisplayName,
  authorPhoto,
  setAuthorPhoto,
  authorCredentials,
  setAuthorCredentials,
  bio,
  setBio,
  onSave,
  isSavingBio,
}) {
  if (!user) return null;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error(
        isSpanish ? 'Imagen demasiado grande (máx 4 MB)' : 'Image too large (max 4 MB)'
      );
      return;
    }
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/auth/author-photo', {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || (isSpanish ? 'No se pudo subir' : 'Upload failed'));
        return;
      }
      setAuthorPhoto(data.url);
      toast.success(isSpanish ? 'Foto subida' : 'Photo uploaded');
    } catch (err) {
      toast.error(isSpanish ? 'No se pudo subir la foto' : 'Photo upload failed');
    }
  };

  const slug = (user.display_name || user.name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const isDirty =
    bio !== (user.bio || '') ||
    authorDisplayName !== (user.author_display_name || '') ||
    authorPhoto !== (user.author_photo || '') ||
    authorCredentials !== (user.author_credentials || '');

  return (
    <Card className="mb-6" data-testid="author-bio-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PenLine className="h-5 w-5 text-[#00E5CC]" />
          {isSpanish ? 'Biografía pública para el blog' : 'Public Author Bio for Blog'}
        </CardTitle>
        <CardDescription>
          {isSpanish
            ? 'Aparece en cada artículo del blog que publiques. Solo visible para administradores y colaboradores.'
            : 'Appears under every blog post you publish. Admins & contributors only.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Author display name */}
        <div className="space-y-1.5">
          <Label htmlFor="author-display-name" className="text-sm font-medium">
            {isSpanish ? 'Nombre para la firma del artículo' : 'Display name on blog byline'}
          </Label>
          <Input
            id="author-display-name"
            value={authorDisplayName}
            onChange={(e) => setAuthorDisplayName(e.target.value.slice(0, 60))}
            placeholder={
              isSpanish ? 'p. ej. Katie (Detective Dino)' : 'e.g. Katie (Detective Dino)'
            }
            maxLength={60}
            data-testid="author-display-name-input"
          />
          <p className="text-xs text-muted-foreground">
            {isSpanish
              ? 'Aparece como autor en cada artículo. Déjalo en blanco para usar tu nombre de la app.'
              : 'Shown as the byline on every post. Leave blank to use your in-app name.'}
          </p>
        </div>

        {/* Author photo */}
        <div className="space-y-1.5">
          <Label htmlFor="author-photo" className="text-sm font-medium">
            {isSpanish ? 'Foto del autor' : 'Author photo'}
          </Label>
          <div className="flex items-center gap-3">
            {authorPhoto ? (
              <img
                src={authorPhoto}
                alt="Author preview"
                className="h-16 w-16 rounded-full object-cover border-2 border-[#00E5CC]"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-muted border-2 border-dashed border-muted-foreground/30 flex items-center justify-center text-[10px] text-muted-foreground text-center px-1">
                {isSpanish ? 'Sin foto' : 'No photo'}
              </div>
            )}
            <input
              id="author-photo-file"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="author-photo-file-input"
              onChange={handleFileChange}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => document.getElementById('author-photo-file').click()}
              data-testid="author-photo-upload-btn"
            >
              {isSpanish ? 'Subir foto' : 'Upload photo'}
            </Button>
            {authorPhoto && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAuthorPhoto('')}
                className="text-red-600 hover:text-red-700"
                data-testid="author-photo-clear-btn"
              >
                {isSpanish ? 'Quitar' : 'Remove'}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {isSpanish
              ? 'Sube una foto cuadrada (máx 4 MB). Si está en blanco se usará tu avatar pixelado.'
              : 'Upload a square photo (max 4 MB). If blank, your pixel avatar is used.'}
          </p>
        </div>

        {/* Credentials */}
        <div className="space-y-1.5">
          <Label htmlFor="author-credentials" className="text-sm font-medium">
            {isSpanish ? 'Credenciales / título' : 'Credentials / degrees'}
          </Label>
          <Input
            id="author-credentials"
            value={authorCredentials}
            onChange={(e) => setAuthorCredentials(e.target.value.slice(0, 120))}
            placeholder={
              isSpanish ? 'p. ej. MSW, Coach de TDAH' : 'e.g. MSW, LCSW, ADHD coach'
            }
            maxLength={120}
            data-testid="author-credentials-input"
          />
          <p className="text-xs text-muted-foreground">
            {isSpanish
              ? 'Aparece debajo del nombre del autor. Genera credibilidad.'
              : 'Shown under the author name. Builds reader trust.'}
          </p>
        </div>

        {/* Bio textarea */}
        <div className="space-y-1.5">
          <Label htmlFor="bio-textarea" className="text-sm font-medium">
            {isSpanish ? 'Biografía corta' : 'Short bio'}
          </Label>
          <Textarea
            id="bio-textarea"
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 1000))}
            placeholder={
              isSpanish
                ? 'Cuéntale al mundo en qué te apasiona escribir, tu experiencia o un dato divertido...'
                : 'Tell readers what you love writing about, your background, or a fun fact...'
            }
            rows={5}
            maxLength={1000}
            className="resize-y"
            data-testid="bio-textarea"
          />
          <div className="flex items-center justify-between text-xs">
            <a
              href={`/author/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00E5CC] hover:underline"
              data-testid="bio-preview-link"
            >
              {isSpanish ? 'Ver mi página de autor →' : 'View my author page →'}
            </a>
            <span
              className={`tabular-nums ${
                bio.length > 900
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground'
              }`}
              data-testid="bio-char-count"
            >
              {bio.length} / 1000
            </span>
          </div>
        </div>

        <Button
          onClick={onSave}
          disabled={isSavingBio || !isDirty}
          className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-semibold"
          data-testid="save-bio-btn"
        >
          {isSavingBio
            ? isSpanish
              ? 'Guardando…'
              : 'Saving…'
            : isSpanish
            ? 'Guardar biografía'
            : 'Save bio'}
        </Button>
      </CardContent>
    </Card>
  );
}
